import { makeAutoObservable, runInAction } from 'mobx';
import { hitApi } from '../database/services/ApiHelper';
import { API } from '../database/apis';
import { DownloadRecord, DefaultAd, Book } from '../types';
import { userStore } from './userStore';
import localforage from 'localforage';

interface PageListItem {
  _id: string;
  cp: number;
}

// Map a D2P /kleverbook/downloads item onto the legacy DownloadRecord shape
// the rest of the kleverbook app already understands (so screens and PdfViewer
// don't need to change). The book payload also picks up the presigned ebookUrl
// directly from the server.
function mapD2pDownloadToRecord(d: any): DownloadRecord {
  const book = d.book || {};
  const mappedBook: Book = {
    _id: book.id,
    title: book.title,
    authorName: book.author?.name ?? 'Anonymous',
    category: book.category,
    categoryName: book.category,
    description: book.description ?? '',
    coverImage: book.coverUrl,
    bookFile: book.ebookUrl,
    pdf_file: book.ebookUrl,
    ad_slots: Array.isArray(book.adSlots) ? book.adSlots.map(String) : [],
  };
  return {
    _id: d.id,
    user: userStore.user?._id ?? '',
    adverbook: mappedBook,
    createdAt: d.downloadedAt,
    updatedAt: d.downloadedAt,
  };
}

class DownloadStore {
  downloads: DownloadRecord[] = [];
  defaultAd: DefaultAd = {};
  defaultAds: DefaultAd[] = [];
  pList: PageListItem[] = [];
  loading = false;

  // Debounce timer for server-side reading-progress sync
  private progressTimers: Record<string, ReturnType<typeof setTimeout>> = {};

  // Pending ad-show tokens keyed by downloadId. Set when /ad-view returns
  // a token and cleared after the next forward progress PATCH consumes it
  // server-side. The reader can't move past a slot page without one.
  private pendingAdTokens: Record<string, string> = {};

  setPendingAdToken(downloadId: string, tokenId: string | null) {
    if (!tokenId) {
      delete this.pendingAdTokens[downloadId];
    } else {
      this.pendingAdTokens[downloadId] = tokenId;
    }
  }

  constructor() {
    makeAutoObservable(this);
    this.hydrateDownloads();
    this.hydratePageList();
  }

  async hydrateDownloads() {
    try {
      const saved = await localforage.getItem<string>('downloads');
      if (saved) {
        runInAction(() => {
          this.downloads = JSON.parse(saved);
        });
      }
    } catch (e) {
      console.log('Download hydration error:', e);
    }
  }

  async hydratePageList() {
    try {
      const saved = await localforage.getItem<string>('pList');
      if (saved) {
        runInAction(() => {
          this.pList = JSON.parse(saved);
        });
      }
    } catch (e) {
      console.log('Page list hydration error:', e);
    }
  }

  async persistDownloads() {
    try {
      await localforage.setItem('downloads', JSON.stringify(this.downloads));
    } catch (e) {
      console.log('Download persist error:', e);
    }
  }

  async persistPageList() {
    try {
      await localforage.setItem('pList', JSON.stringify(this.pList));
    } catch (e) {
      console.log('Page list persist error:', e);
    }
  }

  setPageList(list: PageListItem[]) {
    this.pList = list;
    this.persistPageList();
  }

  getSavedPage(bookId: string): number {
    const item = this.pList.find((p) => p._id === bookId);
    return item ? item.cp : 1;
  }

  saveCurrentPage(bookId: string, page: number) {
    // Local cache (so the reader has an instant page on next open even
    // before the server response comes back).
    const list = this.pList.slice();
    const index = list.findIndex((p) => p._id === bookId);
    if (index > -1) {
      list[index].cp = page;
    } else {
      list.push({ _id: bookId, cp: page });
    }
    this.setPageList(list);

    // Server-side sync (debounced ~600ms per book) so the user can pick up on
    // another device. Identifier is bookId; we look up the matching downloadId.
    const download = this.downloads.find((d) => {
      const ab = d.adverbook;
      const bid = typeof ab === 'object' ? ab._id : ab;
      return bid === bookId;
    });
    if (!download || !userStore.effectiveToken) return;

    if (this.progressTimers[bookId]) {
      clearTimeout(this.progressTimers[bookId]);
    }
    this.progressTimers[bookId] = setTimeout(() => {
      // If an ad just played, attach its token so the server lets us
      // cross the slot. If no token is pending the server will reject
      // forward moves across a slot — which is the whole point.
      const adShowToken = this.pendingAdTokens[download._id];
      const body: any = { lastPage: page };
      if (adShowToken) body.adShowToken = adShowToken;
      hitApi({
        endpoint: API.UPDATE_PROGRESS(download._id),
        method: 'PATCH',
        body,
        token: userStore.effectiveToken,
      })
        .then(() => {
          // Token is single-use — clear it whether the server accepted it
          // or rejected as already-consumed. A still-locked slot will
          // simply mint a new token on the next ad show.
          if (adShowToken) delete this.pendingAdTokens[download._id];
        })
        .catch(() => {
          // Network blip — the next page change will retry.
        });
    }, 600);
  }

  async getDownloadsByUser() {
    // Anon device readers also have downloads — gate only on a token,
    // not on userStore.user.
    if (!userStore.effectiveToken) return;
    this.loading = true;
    try {
      const res = await hitApi({
        endpoint: API.DOWNLOADED_BOOKS,
        method: 'GET',
        token: userStore.effectiveToken,
      });
      const items: any[] = Array.isArray(res?.downloads) ? res.downloads : [];
      runInAction(() => {
        this.downloads = items.map(mapD2pDownloadToRecord);
        // Hydrate the local pList from server-side lastPage so the reader
        // resumes at the right page across devices.
        const updates = new Map<string, number>();
        for (const d of items) {
          if (d?.book?.id && typeof d.lastPage === 'number') {
            updates.set(d.book.id, d.lastPage);
          }
        }
        if (updates.size > 0) {
          const next = this.pList.slice();
          updates.forEach((page, bookId) => {
            const idx = next.findIndex((p) => p._id === bookId);
            if (idx > -1) next[idx].cp = page;
            else next.push({ _id: bookId, cp: page });
          });
          this.pList = next;
          this.persistPageList();
        }
        this.loading = false;
      });
      await this.persistDownloads();
    } catch (error: any) {
      runInAction(() => { this.loading = false; });
    }
  }

  async downloadBook(book: Book, callback: (success: boolean, message?: string) => void) {
    // Anon device sessions can download too — only require a Bearer
    // token (user JWT or device JWT), not a real user record.
    if (!userStore.effectiveToken) {
      callback(false, 'Please reopen the app to start a session');
      return;
    }
    try {
      await hitApi({
        endpoint: API.DOWNLOAD_BOOK,
        method: 'POST',
        body: { bookId: book._id },
        token: userStore.effectiveToken,
      });
      await this.getDownloadsByUser();
      callback(true, 'Book added to your library');
    } catch (error: any) {
      callback(false, error?.data?.error || error?.message || 'Download failed');
    }
  }

  // Live ad serve for a book. Called per slot trigger in the reader so
  // an admin replacing an ad takes effect on the very next slot for
  // in-progress readers. Falls back to the cached default ad when the
  // proprietary pool is empty.
  async serveAd(bookId: string): Promise<DefaultAd | null> {
    try {
      if (!userStore.effectiveToken) return null;
      const res: any = await hitApi({
        endpoint: `${API.ADS_SERVE}?bookId=${encodeURIComponent(bookId)}`,
        method: 'GET',
        token: userStore.effectiveToken,
      });
      const ad = res?.ad;
      if (!ad) return null;
      return {
        _id: ad.id,
        ad_file: ad.mediaUrl,
        type: ad.mediaType,
        ad_time: ad.adTimeSec,
        repeat_time: ad.repeatTimeSec,
        redirect_url: ad.redirectUrl,
      } as any;
    } catch (e) {
      console.log('Live ad serve error:', e);
      return null;
    }
  }

  async getDefaultAds() {
    try {
      const res = await hitApi({
        endpoint: API.DEFAULT_ADS,
        method: 'GET',
      });
      const ad = res?.ad;
      if (!ad) {
        runInAction(() => {
          this.defaultAd = {};
          this.defaultAds = [];
        });
        return;
      }
      // Map D2P ad shape onto legacy DefaultAd fields the PdfViewer reads.
      const mapped: DefaultAd = {
        _id: ad.id,
        ad_file: ad.mediaUrl,
        type: ad.mediaType,
        ad_time: ad.adTimeSec,
        repeat_time: ad.repeatTimeSec,
      };
      runInAction(() => {
        this.defaultAd = mapped;
        this.defaultAds = [mapped];
      });
    } catch (e) {
      console.log('Default ads error:', e);
    }
  }

  async trackAdView(adData: any, downloadId: string, slotPage?: number): Promise<string | null> {
    try {
      // D2P only needs adId — the server already knows the user from the JWT
      // and the book from the download record. Returns adShowToken, which
      // the reader must include in the next progress PATCH that crosses
      // the slot, or the server will refuse the page advance.
      const adId = adData?._id || adData?.id;
      if (!adId || !userStore.effectiveToken) return null;
      const res: any = await hitApi({
        endpoint: API.AD_TRACK(downloadId),
        method: 'POST',
        body: { adId, slotPage: slotPage ?? 0 },
        token: userStore.effectiveToken,
      });
      return res?.adShowToken || null;
    } catch (e) {
      console.log('Ad tracking error:', e);
      return null;
    }
  }
}

export const downloadStore = new DownloadStore();
